import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import usePageContent from '../hooks/usePageContent';

export default function Treatment() {
  const { page, loading } = usePageContent('behandlingen');

  useEffect(() => {
    if (page) {
      document.title = page.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', page.meta_description);
      }
    }
  }, [page]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-brand-beige">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-sage border-t-transparent" />
      </div>
    );
  }

  // Steps of the treatment process
  const steps = [
    {
      num: '1',
      title: 'Modtagelse & Samtale',
      desc: 'Vi starter altid med en kort samtale i rolige omgivelser, hvor vi taler om din kropshistorik, eventuelle spændinger og dine forventninger.'
    },
    {
      num: '2',
      title: 'Behandlingen på briksen',
      desc: 'Du ligger behageligt på en briks under bløde tæpper. Behandlingen udføres med let, løstsiddende tøj på.'
    },
    {
      num: '3',
      title: 'De vigtige pauser',
      desc: 'Mellem serierne af greb træder jeg væk fra kroppen i et par minutter. Dette giver dit nervesystem fuldstændig ro til at integrere impulserne.'
    },
    {
      num: '4',
      title: 'Efterfølgende opmærksomhed',
      desc: 'Vi runder roligt af, og jeg anbefaler, at du drikker rigeligt med vand og tager den med ro resten af dagen.'
    }
  ];

  return (
    <div className="bg-brand-beige">
      {/* Page Header */}
      <section className="bg-brand-cream/30 py-12 md:py-16 border-b border-brand-sage-medium/10">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-sage-dark">
            Forløbet
          </span>
          <h1 className="mt-2 font-serif text-3xl font-semibold text-brand-neutral-dark md:text-4xl lg:text-5xl">
            {page.hero_title}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-brand-neutral-dark/80 md:text-base">
            {page.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Step-by-Step Process Grid */}
      <section className="py-16 md:py-20 bg-brand-cream/10">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-serif text-2xl font-semibold text-brand-neutral-dark md:text-3xl">
              De fire faser i en behandling
            </h2>
            <p className="mt-3 text-sm text-brand-neutral-dark/80">
              Her kan du se, hvordan et typisk besøg hos Stausbroe Bowen Terapi foregår, så du kan føle dig helt tryg.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((step) => (
              <div 
                key={step.num} 
                className="relative rounded-2xl border border-brand-sage-medium/10 bg-white/50 p-6 shadow-sm transition-all duration-300"
              >
                <div className="absolute -top-4 left-6 flex h-8 w-8 items-center justify-center rounded-full bg-brand-sage text-xs font-bold text-white">
                  {step.num}
                </div>
                <h3 className="mt-2 font-serif text-lg font-semibold text-brand-neutral-dark">{step.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-brand-neutral-dark/80">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Content */}
      <section className="py-16 md:py-24 border-t border-brand-sage-medium/10">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            
            {/* Left Image */}
            <div className="relative lg:col-span-5">
              <div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-md md:aspect-[16/10] lg:aspect-square">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/d7ec2ecb-d3f6-4a99-ad2f-51a97712ea7d/491920933-1224764199438362-3389235645605450204-n.jpg"
                  alt="Behandlingsrum med briks og bløde tæpper"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>

            {/* Right Content */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-sage-dark">
                Tryghed & Omsorg
              </span>
              <h2 className="font-serif text-3xl font-semibold text-brand-neutral-dark md:text-4xl">
                Din krop, dit tempo
              </h2>
              <div 
                className="prose prose-stone text-sm leading-relaxed text-brand-neutral-dark/80"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            </div>

          </div>
        </div>
      </section>

      {/* Practical Info Section */}
      <section className="bg-brand-sage-light/30 py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="rounded-3xl bg-white/80 border border-brand-sage-medium/10 p-8 md:p-10 shadow-sm">
            <h3 className="font-serif text-xl font-semibold text-brand-neutral-dark text-center">Praktiske råd før og efter behandling</h3>
            <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
              
              {/* Before */}
              <div>
                <h4 className="font-serif text-base font-semibold text-brand-sage-dark flex items-center gap-2">
                  <i className="fa-light fa-circle-arrow-right" /> Før behandlingen
                </h4>
                <ul className="mt-3 flex flex-col gap-2.5 text-xs text-brand-neutral-dark/80">
                  <li>• Hav let, løstsiddende tøj på (f.eks. bomulds-T-shirt og bløde bukser).</li>
                  <li>• Undgå store måltider lige inden din tid.</li>
                  <li>• Kom gerne 5 minutter før, så du kan nå at lande i ro.</li>
                </ul>
              </div>

              {/* After */}
              <div>
                <h4 className="font-serif text-base font-semibold text-brand-sage-dark flex items-center gap-2">
                  <i className="fa-light fa-circle-arrow-right" /> Efter behandlingen
                </h4>
                <ul className="mt-3 flex flex-col gap-2.5 text-xs text-brand-neutral-dark/80">
                  <li>• Drik rigeligt med vand resten af dagen for at støtte udskilning af affaldsstoffer.</li>
                  <li>• Undgå hård fysisk træning eller varme bade samme dag.</li>
                  <li>• Læg mærke til din krops reaktioner de efterfølgende dage.</li>
                </ul>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-brand-cream/30 py-12 border-t border-brand-sage-medium/10">
        <div className="mx-auto max-w-4xl px-6 text-center flex flex-col items-center gap-4">
          <h2 className="font-serif text-2xl font-semibold text-brand-neutral-dark">
            Har du spørgsmål til forløbet?
          </h2>
          <p className="text-sm text-brand-neutral-dark/80 max-w-xl">
            Du er altid velkommen til at kontakte mig for en uforpligtende snak om, hvordan Bowen Terapi kan hjælpe dig.
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded-full bg-brand-sage px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-300 hover:bg-brand-sage-dark"
            >
              Kontakt mig
            </Link>
            <Link
              to="/priser"
              className="inline-flex items-center justify-center rounded-full border border-brand-sage-medium/30 bg-white/40 px-6 py-2.5 text-sm font-medium text-brand-neutral-dark transition-all duration-300 hover:bg-white/80"
            >
              Se priser
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
